<template>
  <div class="control-manage-page">
    <h2>远程控制平台</h2>
    <div class="form-section">
      <label>选择客户：</label>
      <select v-model="selectedHospitalId" @change="onHospitalChange" :disabled="isLocationUser">
        <option value="">请选择客户</option>
        <option v-for="h in hospitals" :key="h.id" :value="h.id">
          {{ `ID${h.id}/${h.name}` }}
        </option>
      </select>
    </div>
    <div class="form-section" v-if="sensors.length">
      <label>选择传感器：</label>
      <select v-model="selectedSensorId">
        <option value="">请选择传感器</option>
        <option v-for="s in sensors" :key="s.id" :value="s.id">
          {{ `ID${s.id}/${s.name}/${s.mqttTopic}` }}
        </option>
      </select>
    </div>
    <div class="button-section" v-if="selectedSensor">
      <button @click="sendCommand('DO1')">常闭</button>
      <button @click="sendCommand('DO2')">常开</button>
      <button @click="sendCommand('DO3')">单开</button>
      <button @click="sendCommand('DO4')">半开</button>
    </div>
    <div class="button-section" v-if="selectedSensor">
      <button class="reset-btn" @click="sendReset('DO1')">常闭复位</button>
      <button class="reset-btn" @click="sendReset('DO2')">常开复位</button>
      <button class="reset-btn" @click="sendReset('DO3')">单开复位</button>
      <button class="reset-btn" @click="sendReset('DO4')">半开复位</button>
    </div>
  </div>
</template>

<script>
import request from '@/utils/request';
import { WS_BASE_URL } from '@/config'

export default {
  name: 'ControlManagePage',
  data() {
    return {
      hospitals: [],
      selectedHospitalId: '',
      sensors: [],
      selectedSensorId: '',
      ws: null,
    }
  },
  computed: {
    selectedSensor() {
      return this.sensors.find(s => String(s.id) === String(this.selectedSensorId));
    },
    isLocationUser() {
      return localStorage.getItem('userType') === 'location';
    }
  },
  created() {
    if (this.isLocationUser) {
      this.bootstrapHospitalForLocationUser();
    } else {
    this.fetchHospitals();
    }
    this.setupWebSocket();
  },
  methods: {
    // 获取客户列表
    fetchHospitals() {
      request.get('locations/get')
        .then(res => {
          if (res.data.code === 200) {
            this.hospitals = res.data.data;
          }
        });
    },
    bootstrapHospitalForLocationUser() {
      const locationId = localStorage.getItem('locationId');
      if (!locationId) {
        console.warn('未找到 locationId，本地登录信息可能缺失');
        return;
      }
      const locationName = localStorage.getItem('locationName') || `客户 ${locationId}`;
      this.hospitals = [{
        id: locationId,
        name: locationName
      }];
      this.selectedHospitalId = locationId;
      this.onHospitalChange();
    },
    // 客户选择变化时获取传感器
    onHospitalChange() {
      console.log('[Control] 客户选择变化:', this.selectedHospitalId);
      this.selectedSensorId = '';
      if (!this.selectedHospitalId) {
        this.sensors = [];
        return;
      }
      request.get(`sensors/getByLocation?locationId=${this.selectedHospitalId}`)
        .then(res => {
          console.log('[Control] 传感器API响应:', res.data);
          if (res.data.code === 200) {
            // 只取你需要的字段
            this.sensors = res.data.data.map(s => ({
              id: s.id,
              locationId: s.locationId,
              name: s.name,
              imei: s.imei,
              mqttTopic: s.mqttTopic
            }));
            console.log('[Control] 传感器列表:', this.sensors);
          } else {
            console.warn('[Control] 获取传感器失败:', res.data.message);
            this.sensors = [];
          }
        })
        .catch(err => {
          console.error('[Control] 获取传感器出错:', err);
          this.sensors = [];
        });
    },
    setupWebSocket() {
      // 获取JWT token
      const token = localStorage.getItem('jwtToken') || localStorage.getItem('authToken');
      if (!token) {
        console.error('[WebSocket] 未找到JWT Token，无法建立连接');
        return;
      }
      
      // 将token作为查询参数添加到WebSocket URL中
      // 后端会在握手阶段从URL参数中读取并验证token
      // 注意：JWT本身是Base64URL编码，已经是URL安全的，不需要再编码
      const wsUrl = `${WS_BASE_URL}?token=${token}`;
      
      console.log('[WebSocket] 远程控制WebSocket连接:', {
        url: wsUrl.replace(token, 'token=***'),
        hasToken: !!token
      });
      
      this.ws = new WebSocket(wsUrl);
      
      this.ws.onopen = () => {
        console.log('[WebSocket] 远程控制连接成功');
      };
      
      this.ws.onerror = (error) => {
        console.error('[WebSocket] 远程控制连接错误:', error);
      };
      
      this.ws.onclose = (event) => {
        console.warn('[WebSocket] 远程控制连接关闭:', {
          code: event.code,
          reason: event.reason
        });
      };
      
      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          // 处理心跳包：收到heartbeat时回复pong
          if (data.type === 'heartbeat') {
            console.log('[WebSocket] 远程控制收到心跳包，回复pong');
            try {
              this.ws.send(JSON.stringify({ type: 'pong' }));
            } catch (error) {
              console.warn('[WebSocket] 发送pong失败:', error);
            }
            return;
          }
          
          // 判断是否是控制响应
          if (data.CMD === 'yinerda' && data.res === 1 && this.controlPending) {
            this.controlPending = false;
            clearTimeout(this.controlTimeout);
            // 根据 lastControlType 弹窗提示
            let msg = '';
            if (this.lastControlType === 'DO1') msg = '常开模式为您打开';
            if (this.lastControlType === 'DO2') msg = '常闭模式为您打开';
            if (this.lastControlType === 'DO3') msg = '半开模式为您打开';
            // 单开和常开一样
            if (this.lastControlType === 'SINGLE') msg = '单开模式为您打开';
            alert(msg);
          }
        } catch (error) {
          console.warn("忽略非 JSON 消息:", event.data);
        }
      };
    },
    async sendCommand(doKey) {
      if (!this.selectedSensor) {
        alert('请先选择传感器');
        return;
      }
      const mqttTopic = `datedown/${this.selectedSensor.locationId}/${this.selectedSensor.imei}`;
      let controleDate = {};
      if (doKey === 'DO1') controleDate = { DO1: 2 };
      if (doKey === 'DO2') controleDate = { DO2: 2 };
      if (doKey === 'DO3') controleDate = { DO3: 2 };
      if (doKey === 'DO4') controleDate = { DO4: 2 };
      try {
        const response = await request.post('device/send-command', {
          mqttTopic,
          controleDate
        }, {
          headers: { 'Content-Type': 'application/json' },
          timeout: 8000
        });

        console.log('Response:', response.data);

        if (response.data.code === 200) {
          alert('操作成功: ' + response.data.data);
        } else {
          alert('操作失败: ' + (response.data.message || '未知错误'));
        }
      } catch (error) {
        if (error.code === 'ECONNABORTED') {
          alert('操作失败: 响应超时');
        } else if (error.response && error.response.data) {
          const data = error.response.data;
          alert('操作失败: ' + (data.message || '未知错误'));
        } else {
          alert('操作失败: 网络错误');
        }
      }
    },
    async sendReset(doKey) {
      if (!this.selectedSensor) {
        alert('请先选择传感器');
        return;
      }
      const mqttTopic = `datedown/${this.selectedSensor.locationId}/${this.selectedSensor.imei}`;
      let controleDate = {};
      if (doKey === 'DO1') controleDate = { DO1: 1 };
      if (doKey === 'DO2') controleDate = { DO2: 1 };
      if (doKey === 'DO3') controleDate = { DO3: 1 };
      if (doKey === 'DO4') controleDate = { DO4: 1 };

      try {
        const response = await request.post('device/send-command', {
          mqttTopic,
          controleDate
        }, {
          headers: { 'Content-Type': 'application/json' },
          timeout: 8000
        });

        console.log('Reset Response:', response.data);

        if (response.data.code === 200) {
          alert('复位成功: ' + response.data.data);
        } else {
          alert('复位失败: ' + (response.data.message || '未知错误'));
        }
      } catch (error) {
        if (error.code === 'ECONNABORTED') {
          alert('复位失败: 响应超时');
        } else if (error.response && error.response.data) {
          const data = error.response.data;
          alert('复位失败: ' + (data.message || '未知错误'));
        } else {
          alert('复位失败: 网络错误');
        }
      }
    }
  }
}
</script> 

<style scoped>
.control-manage-page {
  max-width: 600px;
  margin: 40px auto 0 auto;
  padding: 40px 32px 32px 32px;
  background: rgba(0, 243, 255, 0.04);
  border-radius: 18px;
  box-shadow: 0 0 24px #00f3ff22, 0 2px 16px #000a;
  display: flex;
  flex-direction: column;
  align-items: center;
}

h2 {
  text-align: center;
  color: #00f3ff;
  margin-bottom: 32px;
  font-size: 2em;
  letter-spacing: 2px;
}

.form-section {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 32px;
  font-size: 1.15em;
}

label {
  margin-right: 18px;
  min-width: 100px;
  color: #00f3ff;
  font-weight: bold;
  font-size: 1.1em;
}

select {
  min-width: 320px;
  padding: 8px 12px;
  font-size: 1.1em;
  border-radius: 6px;
  border: 1.5px solid #00f3ff88;
  background: #181f3a;
  color: #eaf6ff;
  margin-bottom: 0;
  outline: none;
  transition: border 0.2s;
  position: relative;
  z-index: 10;
}

select:focus {
  border: 1.5px solid #00f3ff;
  z-index: 20;
}

select option {
  background: #181f3a;
  color: #eaf6ff;
  padding: 8px;
}

.button-section {
  width: 100%;
  display: flex;
  justify-content: center;
  gap: 32px;
  margin-top: 40px;
}

button {
  padding: 12px 36px;
  background: #00f3ff;
  color: #1a1f4b;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: bold;
  font-size: 1.15em;
  box-shadow: 0 2px 8px #00f3ff33;
  transition: background 0.2s, color 0.2s, box-shadow 0.2s;
}

button:hover {
  background: #0099cc;
  color: #fff;
  box-shadow: 0 4px 16px #00f3ff66;
}

.reset-btn {
  background: #fff;
  color: #00f3ff;
  border: 2px solid #00f3ff;
  font-size: 1em;
  padding: 10px 28px;
  border-radius: 8px;
  margin-top: 0;
  margin-bottom: 0;
  transition: background 0.2s, color 0.2s, border 0.2s;
}

.reset-btn:hover {
  background: #00f3ff;
  color: #fff;
  border: 2px solid #0099cc;
}
</style> 